/**
 * Battle panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import {
	PS, PSRoom, type RoomOptions, type RoomID, Config, type BattlePanelLayout,
} from "./client-main";
import { PSIcon, PSPanelWrapper, PSRoomPanel } from "./panels";
import { ChatLog, ChatRoom, ChatTextEntry, ChatUserList } from "./panel-chat";
import { FormatDropdown } from "./panel-mainmenu";
import { Battle, type Pokemon, type ServerPokemon } from "./battle";
import { BattleScene } from "./battle-animations";
import { Dex, TL, toID, type ID } from "./battle-dex";
import {
	BattleChoiceBuilder, type BattleRequestActivePokemon, type BattleRequestSideInfo,
	type BattleRequest, type BattleMoveRequest, type BattleSwitchRequest, type BattleTeamRequest,
} from "./battle-choices";
import { BattleTextParser, type Args } from "./battle-text-parser";
import { ModifiableValue } from "./battle-tooltips";
import { Net } from "./client-connection";
import { BattleLog } from "./battle-log";
import { BattleTeraTracker, BattleToolsPanel } from './panel-battle-tools';

type BattleDesc = {
	id: RoomID,
	minElo?: number | string,
	p1?: string,
	p2?: string,
	p3?: string,
	p4?: string,
};
export class BattlesRoom extends PSRoom {
	override readonly classType = 'battles';
	/** null means still loading */
	format = '';
	filters = '';
	battles: BattleDesc[] | null = null;
	constructor(options: RoomOptions) {
		super(options);
		this.refresh();
		// If graphics preference is set to use BW sprites
		if (PS.prefs.bwgfx) {
			Dex.loadSpriteData('bw');
		}
	}
	setFormat(format: string) {
		if (format === this.format) return this.refresh();
		this.battles = null;
		this.format = format;
		this.update(null);
		this.refresh();
	}
	refresh() {
		PS.send(`/cmd roomlist ${toID(this.format)}, ${this.filters}`);
	}
}

class BattlesPanel extends PSRoomPanel<BattlesRoom> {
	static readonly id = 'battles';
	static readonly routes = ['battles'];
	static readonly Model = BattlesRoom;
	static readonly location = 'right';
	static readonly icon = <i class="fa fa-caret-square-o-right" aria-hidden></i>;
	static readonly title = 'Battles';
	static getTitle() {
		return TL`Battles`;
	}
	refresh = () => {
		this.props.room.refresh();
	};
	changeFormat = (e: Event) => {
		const value = (e.target as HTMLButtonElement).value;
		this.props.room.setFormat(value);
	};
	applyFilters = (e: Event) => {
		e.preventDefault();
		const minElo = this.base?.querySelector<HTMLInputElement>(`select[name=elofilter]`)?.value;
		const searchPrefix = this.base?.querySelector<HTMLInputElement>(`input[name=prefixsearch]`)?.value;
		this.props.room.filters = `${minElo || ''},${searchPrefix || ''}`;
		this.refresh();
	};
	renderBattleLink(battle: BattleDesc) {
		const format = battle.id.split('-')[1];
		const minEloMessage = typeof battle.minElo === 'number' ? TL`rated ${battle.minElo}` : battle.minElo;
		const players = TL`${battle.p1} vs. ${battle.p2}`;
		return <div key={battle.id}><a href={`/${battle.id}`} class="blocklink">
			{minEloMessage && <small style="float:right">({minEloMessage})</small>}
			<small>[{format}]</small><br />
			<em>{players}</em>
		</a></div>;
	}
	override render() {
		const room = this.props.room;
		return <PSPanelWrapper room={room}><div class="pad">
			<button class="button" style="float:right;font-size:10pt;margin-top:3px" name="closeRoom">
				<i class="fa fa-times" aria-hidden></i> {TL`[Close]`}
			</button>
			<div class="roomlist">
				<p>
					<button class="button" name="refresh" onClick={this.refresh}>
						<i class="fa fa-refresh" aria-hidden></i> {TL`[Refresh]`}
					</button> {}
					<span
						style={Dex.getPokemonIcon('meloetta-pirouette') + ';display:inline-block;vertical-align:middle'} class="picon"
						title={TL`Meloetta is PS's mascot! The Pirouette forme is Fighting-type, and represents our battles.`}
					></span>
				</p>

				<p>
					<label class="label">{TL.label(TL`Format`)}</label>
					<FormatDropdown onChange={this.changeFormat} placeholder={TL`(All formats)`} />
				</p>
				<label class="label">
					{TL.label(TL`Minimum Elo`)}<select name="elofilter" class="select" onChange={this.applyFilters}>
						<option value="none">{TL`None`}</option><option value="1100">1100</option><option value="1300">1300</option>
						<option value="1500">1500</option><option value="1700">1700</option><option value="1900">1900</option>
					</select>
				</label>

				<form class="search" onSubmit={this.applyFilters}>
					<p>
						<input type="text" name="prefixsearch" class="textbox" placeholder={TL`Username prefix`} autocomplete="off" />
						<button type="submit" class="button">{TL`[Search]`}</button>
					</p>
				</form>
				<div class="list">{!room.battles ? (
					<p>{TL`Loading...`}</p>
				) : !room.battles.length ? (
					<p>{TL`No battles are going on`}</p>
				) : (<>
					<p>{room.battles.length === 1 ? (
						TL`${1} battle`
					) : (
						TL`${room.battles.length === 100 ? '100+' : room.battles.length} battles`
					)}</p>
					{room.battles.map(battle => this.renderBattleLink(battle))}
				</>
				)}</div>
			</div>
		</div></PSPanelWrapper>;
	}
}

export class BattleRoom extends ChatRoom {
	override readonly classType = 'battle';
	declare pmTarget: null;
	declare challengeMenuOpen: false;
	declare challengingFormat: null;
	declare challengedFormat: null;

	override battle: Battle = null!;
	/** null if spectator, otherwise current player's info */
	side: BattleRequestSideInfo | null = null;
	request: BattleRequest | null = null;
	choices: BattleChoiceBuilder | null = null;
	autoTimerActivated: boolean | null = null;
	requireForfeit = false;
	/** Public timer notices only; the server does not expose the opponent's full clock. */
	opponentClock: { userid: string, seconds: number, receivedAt: number } | null = null;
	opponentTimerEnabled = false;
	opponentRequestId: number | null = null;
	opponentWaitStartedAt: number | null = null;
	trackOpponentClock(args: Args) {
		let newRequest = false;
		if (args[0] === 'request') {
			const request = args[1] ? JSON.parse(args[1]) as BattleRequest : null;
			newRequest = request?.rqid !== this.opponentRequestId;
			this.opponentRequestId = request?.rqid ?? null;
		}
		if (newRequest || ['turn', 'win', 'tie', 'start', 'init', 'inactiveoff'].includes(args[0])) {
			this.opponentClock = null;
			this.opponentWaitStartedAt = null;
		}
		if (args[0] === 'inactiveoff') this.opponentTimerEnabled = false;
		if (args[0] !== 'inactive') return;
		this.opponentTimerEnabled = true;
		const match = /^(.*?) (?:has|reconnected and has) (\d+) seconds left(?: this turn)?\.$/.exec(args[1]);
		if (!match) return;
		const userid = toID(match[1]);
		if (userid === PS.user.userid) return;
		this.opponentClock = { userid, seconds: Number(match[2]), receivedAt: Date.now() };
	}
	updateOpponentWait() {
		const waiting = this.request && !this.battle?.ended &&
			(this.request.requestType === 'wait' || this.choices?.isDone());
		if (!waiting) this.opponentWaitStartedAt = null;
		else if (this.opponentWaitStartedAt == null) this.opponentWaitStartedAt = Date.now();
	}
	/** should be false if we joined right after accepting or challenging a battle,
	  * and true if we refreshed and rejoined a battle.
		* null = initializing, we don't know yet */
	rejoining: boolean | null = null;
	overlayActive: 'move' | 'switch' | null = null;

	private pendingLines: Args[] = [];
	/** showdex compat */
	onRequest: ((request: BattleRequest | null) => void) | null = null;

	override receiveBatch(batch: Args[]) {
		for (const args of batch) (this as any).receiveLine(args); // showdex compat
		if (!this.battle) {
			this.pendingLines.push(...batch);
			this.update(null);
			return;
		}
		if (this.pendingLines.length) {
			batch = [...this.pendingLines, ...batch];
			this.pendingLines = [];
		}
		if (!batch.length) {
			this.update(null);
			return;
		}

		const battleLines: string[] = [];
		const controlLines: Args[] = [];
		for (const args of batch) {
			this.trackOpponentClock(args);
			if (this.handleLine(args)) continue;
			switch (args[0]) {
			case 'request': case 'sentchoice': case 'initdone':
				controlLines.push(args);
				continue;
			case 'win': case 'tie': case 'error':
				controlLines.push(args);
				break;
			}
			battleLines.push('|' + args.join('|'));
		}
		this.battle.addBatch(battleLines);
		// delayed to after `battle.addBatch` because these can depend on battle state
		for (const args of controlLines) this.handleLineAfterBattleUpdate(args);
		if (PS.prefs.noanim || (this.rejoining && this.side)) {
			this.battle.seekTurn(Infinity);
		}
		if (this.side) this.rejoining = false;
		// A reconnect can send |sentchoice| immediately after |request|, so do this after the batch
		this.updateChoiceNotification();
		this.update(null);
	}
	override handleLine(args: Args): boolean {
		if (super.handleLine(args)) return true;
		switch (args[0]) {
		case 'cantleave':
			this.requireForfeit = true;
			return true;
		case 'allowleave':
			this.requireForfeit = false;
			return true;
		}
		return false;
	}
	handleLineAfterBattleUpdate(args: Args) {
		switch (args[0]) {
		case 'initdone':
			if (!PS.prefs.spectatefromstart) this.battle.seekTurn(Infinity);
			break;
		case 'request': case 'win': case 'tie': {
			const request = args[0] === 'request' && args[1] ? JSON.parse(args[1]) : null;
			this.receiveRequest(request);
			this.onRequest?.(request);
			break;
		}
		case 'error':
			if (args[1].startsWith('[Invalid choice]') && this.request) {
				this.choices = new BattleChoiceBuilder(this.request);
			}
			break;
		case 'sentchoice':
			if (this.request) {
				let choices = new BattleChoiceBuilder(this.request);
				const possibleError = choices.addChoices(args[1]);
				if (possibleError || !choices.isDone()) {
					choices = new BattleChoiceBuilder(this.request);
					choices.serializedChoice = args[1];
				}
				this.choices = choices;
			}
			break;
		}
	}
	receiveRequest(request: BattleRequest | null) {
		if (!request) {
			this.request = null;
			this.choices = null;
			return;
		}

		if (PS.prefs.autotimer && !this.battle.kickingInactive && !this.autoTimerActivated) {
			this.send('/timer on');
			this.autoTimerActivated = true;
		}

		BattleChoiceBuilder.fixRequest(request, this.battle);

		if (request.side) {
			this.battle.myPokemon = request.side.pokemon;
			this.battle.setViewpoint(request.side.id);
			this.side = request.side;
		}
		if (request.ally) {
			this.battle.myAllyPokemon = request.ally.pokemon;
		}

		this.request = request;
		this.choices = new BattleChoiceBuilder(request);
	}

	override interruptClose(explicit?: boolean, elem?: HTMLElement | null) {
		if (this.isPlaying() || this.requireForfeit) {
			PS.join('forfeitbattle' as RoomID, { parentElem: elem, parentRoomid: this.id });
			return TL`You are still in ${this.title}`;
		}
		return super.interruptClose(explicit, elem);
	}
	isPlaying() {
		return this.battle && !this.battle.ended && this.request && this.connectMode !== 'deleted';
	}
	updateChoiceNotification() {
		this.updateOpponentWait();
		const oName = this.battle?.farSide.name;
		let title = '';
		let body = '';
		switch (this.request?.requestType) {
		case 'move':
			title = BattleTextParser.ui('notifyMoveTitle');
			body = oName ? BattleTextParser.ui('notifyMoveAgainst', { OPPONENT: oName }) : BattleTextParser.ui('notifyMove');
			break;
		case 'switch':
			title = BattleTextParser.ui('notifySwitchTitle');
			body = oName ? BattleTextParser.ui('notifySwitchAgainst', { OPPONENT: oName }) : BattleTextParser.ui('notifySwitch');
			break;
		case 'team':
			title = BattleTextParser.ui('notifyTeamTitle');
			body = oName ? BattleTextParser.ui('notifyTeamAgainst', { OPPONENT: oName }) : BattleTextParser.ui('notifyTeam');
			break;
		}

		if (!this.choices || this.choices.isDone()) body = '';

		const current = this.notifications.find(notification => notification.id === 'choice');
		if ((current?.body || '') === body) return;

		if (!body) {
			this.dismissNotification('choice');
		} else {
			this.notify({ title, body, id: 'choice', noAutoDismiss: true });
		}
	}

	override handleReconnect(): boolean | void {
		this.opponentClock = null;
		this.opponentRequestId = null;
		this.opponentWaitStartedAt = null;
		this.pendingLines = [];
		if (this.battle) {
			this.battle.stepQueue = [];
			this.battle.preemptStepQueue = [];
			this.battle.resetStep();
		}
		this.side = null;
		this.request = null;
		this.choices = null;
		this.updateChoiceNotification();
		return false;
	}

	override destroy() {
		this.request = null;
		this.choices = null;
		super.destroy();
	}

	loadReplay() {
		const replayid = this.id.slice(7);
		Net(`https://replay.pokemonshowdown.com/${replayid}.json`).get().catch(() => '').then(data => {
			try {
				const replay = JSON.parse(data);
				const [player1, player2] = replay.players;
				const players = replay.players.length === 2 ? TL`${player1} vs. ${player2}` : replay.players.join(' vs. ');
				this.title = `[${replay.format}] ${players}`;
				this.battle.stepQueue = replay.log.split('\n');
				this.battle.atQueueEnd = false;
				this.battle.pause();
				this.battle.seekTurn(0);
				this.connectMode = null;
				this.connectError = null;
				this.update(null);
			} catch {
				this.connectError = TL`Battle "${replayid}" not found`;
				if (!this.battle.stepQueue.length) {
					this.battle.scene.message(
						`<div class="broadcast-red pad"><strong>${BattleLog.escapeHTML(this.connectError)}</strong></div><br />` +
						`${TL`The battle you're looking for has expired. Battles expire after 15 minutes of inactivity unless they're saved.`}<br /><br />` +
						TL`In the future, remember to click "Save replay" to save a replay permanently.`
					);
				}
				this.update(null);
			}
		});
	}
}

class BattleDiv extends preact.Component<{ room: BattleRoom }> {
	override shouldComponentUpdate() {
		return false;
	}
	override componentDidMount() {
		const room = this.props.room;
		if (room.battle) {
			this.base!.replaceChild(room.battle.scene.$frame![0], this.base!.firstChild!);
		}
	}
	override render() {
		return <div><div class="battle"></div></div>;
	}
}

class TimerButton extends preact.Component<{ room: BattleRoom, top: number }> {
	timerInterval: ReturnType<typeof setInterval> | null = null;
	override componentWillUnmount() {
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}
	secondsToTime(seconds: number | true) {
		if (seconds === true) return '-:--';
		const minutes = Math.floor(seconds / 60);
		seconds -= minutes * 60;
		return `${minutes}:${(seconds < 10 ? '0' : '')}${seconds}`;
	}
	render() {
		let time = TL`Timer`;
		const room = this.props.room;
		if (!this.timerInterval && room.battle.kickingInactive) {
			this.timerInterval = setInterval(() => {
				if (room.choices?.isDone()) return;
				if (typeof room.battle.kickingInactive === 'number' && room.battle.kickingInactive > 1) {
					room.battle.kickingInactive--;
					if (room.battle.graceTimeLeft) room.battle.graceTimeLeft--;
					else if (room.battle.totalTimeLeft) room.battle.totalTimeLeft--;
				}
				this.forceUpdate();
			}, 1000);
		} else if (this.timerInterval && !room.battle.kickingInactive) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}

		let timerTicking = (room.battle.kickingInactive &&
			room.request && room.request.requestType !== "wait" && (room.choices && !room.choices.isDone())) ?
			' timerbutton-on' : '';

		if (room.battle.kickingInactive) {
			const secondsLeft = room.battle.kickingInactive;
			time = this.secondsToTime(secondsLeft);
			if (secondsLeft !== true) {
				if (secondsLeft <= 10 && timerTicking) {
					timerTicking = ' timerbutton-critical';
				}

				if (room.battle.totalTimeLeft) {
					const totalTime = this.secondsToTime(room.battle.totalTimeLeft);
					time += ` |  ${totalTime} total`;
				}
			}
		}

		return <button
			style={{ position: "absolute", right: '10px', top: `${this.props.top}px` }}
			data-href="battletimer" class={`button${timerTicking}`} role="timer"
		>
			<i class="fa fa-hourglass-start" aria-hidden></i> {time}
		</button>;
	}
};

class BattlePanel extends PSRoomPanel<BattleRoom> {
	static readonly id = 'battle';
	static readonly routes = ['battle-*', 'game-*'];
	static readonly Model = BattleRoom;
	static getTitle(room: PSRoom) {
		const { battle, title } = room as BattleRoom;
		if (title === 'Uploaded replay') return TL`Uploaded replay`;

		const p1 = battle?.sides[0]?.name;
		const p2 = battle?.sides[1]?.name;
		if (!p1 || !p2) return title;

		const [, prefix = '', mainTitle] = /^(\[[^\]]*\] )?([^]*)$/.exec(title)!;
		if (battle.gameType === 'multi' && mainTitle === `Team ${p1} vs. Team ${p2}`) {
			const player1 = TL`Team ${p1}`;
			const player2 = TL`Team ${p2}`;
			return prefix + TL`${player1} vs. ${player2}`;
		} else if (battle.gameType === 'freeforall' && mainTitle === `${p1} and friends`) {
			return prefix + TL`${p1} and friends`;
		} else if (mainTitle === `${p1} vs. ${p2}`) {
			return prefix + TL`${p1} vs. ${p2}`;
		}
		return title;
	}
	static handleDrop(ev: DragEvent) {
		const file = ev.dataTransfer?.files?.[0];
		if (file?.type === 'text/html') {
			let roomNum = 1;
			for (; roomNum < 100; roomNum++) {
				if (!PS.rooms[`battle-uploaded-${roomNum}`]) break;
			}
			file.text().then(html => {
				const titleStart = html.indexOf('<title>');
				const titleEnd = html.indexOf('</title>');
				let title = 'Uploaded replay';
				if (titleStart >= 0 && titleEnd > titleStart) {
					title = html.slice(titleStart + 7, titleEnd - 1);
					const colonIndex = title.indexOf(':');
					const hyphenIndex = title.lastIndexOf('-');
					if (hyphenIndex > colonIndex + 2) {
						title = title.substring(colonIndex + 2, hyphenIndex - 1);
					} else {
						title = title.substring(colonIndex + 2);
					}
				}
				const index1 = html.indexOf('<script type="text/plain" class="battle-log-data">');
				const index2 = html.indexOf('<script type="text/plain" class="log">');
				if (index1 < 0 && index2 < 0) {
					PS.alert(TL`Unrecognized HTML file: Only replay files are supported.`);
					return;
				}
				if (index1 >= 0) {
					html = html.slice(index1 + 50);
				} else if (index2 >= 0) {
					html = html.slice(index2 + 38);
				}
				const index3 = html.indexOf('</script>');
				html = html.slice(0, index3);
				html = html.replace(/\\\//g, '/');

				PS.join(`battle-uploaded-${roomNum}` as RoomID);
				const room = PS.rooms[`battle-uploaded-${roomNum}`] as BattleRoom;
				if (!room) return;

				room.title = title;
				room.connectMode = null;
				PS.receive(`>battle-uploaded-${roomNum}\n${html}`);
			});
			return true;
		}
	}
	renderUIText(id: string, values?: { [placeholder: string]: string | undefined }) {
		return <span
			dangerouslySetInnerHTML={{
				__html: BattleLog.parseLogMessage(BattleTextParser.ui(id, values))[0],
			}}
		></span>;
	}
	/** last displayed team. will not show the most recent request until the last one is gone. */
	team: ServerPokemon[] | null = null;
	mobileChatShown = false;
	mobilePane: 'moves' | 'switch' | 'tools' | 'log' = 'moves';
	battleToolsOpened = false;
	desktopToolsOpen = false;
	closeBattleTools = () => { this.desktopToolsOpen = false; this.forceUpdate(); };
	mobileInspect = false;
	mobileDetailHTML: string | null = null;
	closeMobileDetails = () => {
		this.mobileDetailHTML = null;
		this.forceUpdate();
	};
	isMobileBattle() {
		const { width, height } = this.props.room;
		return (width < 600 && height > width) || (width < 1000 && height < 500);
	}
	mobileSwipe: { x: number, y: number, time: number, sheet: boolean } | null = null;
	startMobileSwipe = (ev: TouchEvent) => {
		this.mobileSwipe = null;
		const target = ev.target as HTMLElement;
		if (ev.touches.length !== 1 || target.closest('input, textarea, select, a')) return;
		const touch = ev.touches[0];
		// Leave the screen edges to browser back/forward navigation.
		if (touch.clientX < 24 || touch.clientX > window.innerWidth - 24) return;
		ev.stopPropagation(); // This region navigates battle panes, not the room sidebar.
		this.mobileSwipe = {
			x: touch.clientX, y: touch.clientY, time: Date.now(),
			sheet: !!target.closest('.mobile-details-heading'),
		};
	};
	cancelMobileSwipe = () => { this.mobileSwipe = null; };
	endMobileSwipe = (ev: TouchEvent) => {
		const start = this.mobileSwipe;
		this.mobileSwipe = null;
		if (!start || ev.touches.length || ev.changedTouches.length !== 1 || Date.now() - start.time > 700) return;
		const dx = ev.changedTouches[0].clientX - start.x;
		const dy = ev.changedTouches[0].clientY - start.y;
		const tab = (ev.target as HTMLElement).closest<HTMLButtonElement>('.mobile-battle-tabs button[value]');
		if (tab && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
			ev.preventDefault();
			this.mobilePane = tab.value as typeof this.mobilePane;
			if (this.mobilePane === 'tools') this.battleToolsOpened = true;
			(this.props.room.battle?.scene as BattleScene)?.tooltips.hideTooltip();
			this.forceUpdate();
			return;
		}
		if (start.sheet) {
			if (dy < 64 || dy < Math.abs(dx) * 1.5) return;
			ev.preventDefault();
			this.closeMobileDetails();
			return;
		}
		if (Math.abs(dx) < 64 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
		ev.preventDefault(); // A swipe starting on a choice must never submit it.
		const panes = ['moves', 'switch', 'tools', 'log'] as const;
		const current = this.props.room.request?.requestType === 'switch' && this.mobilePane === 'moves' ?
			'switch' : this.mobilePane;
		this.mobilePane = panes[Math.max(0, Math.min(3, panes.indexOf(current) + (dx < 0 ? 1 : -1)))];
		if (this.mobilePane === 'tools') this.battleToolsOpened = true;
		(this.props.room.battle?.scene as BattleScene)?.tooltips.hideTooltip();
		this.forceUpdate();
	};
	setMobilePane = (ev: Event) => {
		this.mobilePane = (ev.currentTarget as HTMLButtonElement).value as typeof this.mobilePane;
		if (this.mobilePane === 'tools') this.battleToolsOpened = true;
		(this.props.room.battle?.scene as BattleScene)?.tooltips.hideTooltip();
		this.forceUpdate();
	};
	toggleMobileInspect = () => {
		this.mobileInspect = !this.mobileInspect;
		(this.props.room.battle?.scene as BattleScene)?.tooltips.hideTooltip();
		this.forceUpdate();
	};
	inspectMobileChoice = (ev: MouseEvent) => {
		if (!this.isMobileBattle() || !this.mobileInspect) return;
		this.showMobileDetails(ev);
	};
	showMobileDetails = (ev: MouseEvent) => {
		const tooltips = (this.props.room.battle.scene as BattleScene).tooltips;
		tooltips.showTooltip(ev.currentTarget as HTMLElement);
		// Reuse the engine's formatted, escaped details in a touch-friendly modal.
		this.mobileDetailHTML = document.querySelector('#tooltipwrapper .tooltip')?.innerHTML || null;
		tooltips.hideTooltip();
		this.forceUpdate();
	};
	showMobileChat = () => {
		this.mobileChatShown = true;
		this.forceUpdate();
	};
	showMobileBattle = () => {
		this.mobileChatShown = false;
		this.forceUpdate();
	};
	send = (text: string, elem?: HTMLElement) => {
		this.props.room.send(text, elem);
	};
	focusIfNoSelection = () => {
		if (window.getSelection?.()?.type === 'Range') return;
		this.focus();
	};
	onKey = (e: KeyboardEvent) => {
		if (e.keyCode === 33) { // Pg Up key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop - chatLog.offsetHeight + 60;
			return true;
		} else if (e.keyCode === 34) { // Pg Dn key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop + chatLog.offsetHeight - 60;
			return true;
		}
		return false;
	};
	toggleBoostedMove = (e: Event) => {
		const checkbox = e.currentTarget as HTMLInputElement;
		const choices = this.props.room.choices;
		if (!choices) return; // shouldn't happen
		switch (checkbox.name) {
		case 'mega':
			choices.current.mega = checkbox.checked;
			break;
		case 'megax':
			choices.current.megax = checkbox.checked;
			choices.current.megay = false;
			break;
		case 'megay':
			choices.current.megay = checkbox.checked;
			choices.current.megax = false;
			break;
		case 'ultra':
			choices.current.ultra = checkbox.checked;
			break;
		case 'z':
			choices.current.z = checkbox.checked;
			break;
		case 'max':
			choices.current.max = checkbox.checked;
			break;
		case 'tera':
			choices.current.tera = checkbox.checked;
			break;
		}
		this.props.room.update(null);
	};
	override componentDidMount() {
		const room = this.props.room;
		const $elem = $(this.base!);
		const battle = (room.battle ||= new Battle({
			id: room.id as any,
			$frame: $elem.find('.battle'),
			$logFrame: $elem.find('.battle-log'),
		}));
		const scene = battle.scene as BattleScene;
		room.log = scene.log;
		room.log.getHighlight = room.handleHighlight;
		scene.tooltips.unlisten(scene.$frame);
		scene.tooltips.listen(this.base!);
		battle.subscribe(() => this.forceUpdate());
		room.onRequest = request => {
			// Wait for the next move request, so doubles and forced switches finish
			// collecting all choices before returning to the move controls.
			if (request?.requestType === 'move' && this.mobilePane === 'switch') {
				this.mobilePane = 'moves';
				this.mobileInspect = false;
				this.mobileDetailHTML = null;
				scene.tooltips.hideTooltip();
			}
			(this as any).receiveRequest(request); // showdex compat
		};
		room.receiveBatch([]);
		super.componentDidMount();
		this.forceUpdate();
		if (!PS.prefs.spectatefromstart) battle.seekTurn(Infinity);
		if (PS.prefs.autohardcore) {
			battle.setHardcoreMode(true);
		}
	}
	override componentWillUnmount() {
		this.props.room.onRequest = null;
		const scene = this.props.room.battle?.scene as BattleScene | undefined;
		if (this.base) scene?.tooltips.unlisten(this.base);
		super.componentWillUnmount();
	}
	battleHeight = 360;
	updateLayout() {
		if (!this.base) return;
		const room = this.props.room;
		if (!room.width) return;
		const { battleHeight } = this.chooseLayout();
		this.battleHeight = battleHeight;
		if (battleHeight !== 360) {
			room.battle?.scene.$frame!.css('transform', `scale(${battleHeight / 360})`);
		} else {
			room.battle?.scene.$frame!.css('transform', 'none');
		}
	}
	chooseLayout(): {
		layout: BattlePanelLayout,
		battleHeight: number,
		battleWidth: number,
		overlayControls: boolean,
	} {
		const room = this.props.room;
		if (this.isMobileBattle()) {
			const width = Math.min(room.width, document.documentElement.clientWidth);
			const battleWidth = room.width > room.height ?
				Math.min(Math.floor(width * 0.46), Math.floor(Math.max(100, room.height - 160) * 640 / 360)) :
				Math.min(width, Math.floor(room.height * (this.mobilePane === 'tools' ? 0.31 : 0.50)));
			return { layout: 'top-and-bottom', battleWidth, battleHeight: Math.round(battleWidth * 360 / 640),
				overlayControls: false };
		}
		return PS.chooseBattleLayout(room.width, room.height, PS.prefs.battlelayout);
	}
	/** @deprecated ONLY FOR SHOWDEX */
	private receiveRequest(request: BattleRequest | null) {}
	renderConnectError() {
		const room = this.props.room;
		if (room.connectMode !== 'deleted' && room.connectMode !== 'not-found') {
			return null;
		}
		return <div class="pad"><div class="broadcast-red pad">
			<h3>{room.connectError || TL`Error`}</h3>
			<p class="buttonbar"><button class="button" data-cmd="/close"><strong>{TL`[Close]`}</strong></button></p>
		</div></div>;
	}
	renderControls(overlayVersion = false, hidePlayerControls = false) {
		const battle = this.props.room.battle;
		const controls = this.renderBattleControls(overlayVersion, hidePlayerControls);
		if (!battle || overlayVersion || this.isMobileBattle()) return controls;
		return <>
			<div class="desktop-battle-tools-bar">
				<BattleTeraTracker battle={battle} />
				<button class="button" onClick={() => { this.desktopToolsOpen = true; this.forceUpdate(); }}>
					<i class="fa fa-calculator" aria-hidden></i> Battle tools
				</button>
			</div>
			{this.desktopToolsOpen && <dialog
				class="desktop-battle-tools-dialog" aria-label="Battle tools"
				ref={(dialog: HTMLDialogElement | null) => {
					if (!dialog) return;
					dialog.oncancel = this.closeBattleTools;
					dialog.onclose = this.closeBattleTools;
					if (!dialog.open) dialog.showModal();
				}}
			>
				<div class="battle-tools-dialog-heading"><strong>Battle tools</strong>
					<button class="button" onClick={this.closeBattleTools} autofocus>Close</button></div>
				<BattleToolsPanel battle={battle} />
			</dialog>}
			{controls}
		</>;
	}
	renderBattleControls(overlayVersion = false, hidePlayerControls = false) {
		const room = this.props.room;
		if (!room.battle) return null;
		if (overlayVersion) {
			if (!room.side || !room.request || room.battle.ended) return null;
			return this.renderPlayerControls(room.request, true);
		}
		if (room.battle.ended) return this.renderAfterBattleControls();
		if (room.side && room.request) {
			if (hidePlayerControls) return null;
			return this.renderPlayerControls(room.request);
		}
		if (room.battle.stepQueue.length === 0) return null;

		const atStart = !room.battle.started;
		const atEnd = room.battle.atQueueEnd;
		return <div class="inline-controls battle-playback-controls">
			<p class="battle-action-grid">
				{atEnd ? (
					<button class="button disabled" aria-disabled data-cmd="/play" style="min-width:4.5em">
						<i class="fa fa-play" aria-hidden></i><br />{TL`[Play]`}
					</button>
				) : room.battle.paused ? (
					<button class="button" data-cmd="/play" style="min-width:4.5em">
						<i class="fa fa-play" aria-hidden></i><br />{TL`[Play]`}
					</button>
				) : (
					<button class="button" data-cmd="/pause" style="min-width:4.5em">
						<i class="fa fa-pause" aria-hidden></i><br />{TL`[Pause]`}
					</button>
				)} {}
				{!room.battle.hardcoreMode && <>
					<button class={"button button-first" + (atStart ? " disabled" : "")} data-cmd="/ffto 0" style="margin-right:2px">
						<i class="fa fa-undo" aria-hidden></i><br />{TL`[First turn]`}
					</button>
					<button class={"button button-first" + (atStart ? " disabled" : "")} data-cmd="/ffto -1">
						<i class="fa fa-step-backward" aria-hidden></i><br />{TL`[Prev turn]`}
					</button>
					<button class={"button button-last" + (atEnd ? " disabled" : "")} data-cmd="/ffto +1" style="margin-right:2px">
						<i class="fa fa-step-forward" aria-hidden></i><br />{TL`[Skip turn]`}
					</button>
					<button class={"button button-last" + (atEnd ? " disabled" : "")} data-cmd="/ffto end">
						<i class="fa fa-fast-forward" aria-hidden></i><br />{TL`[Skip to end]`}
					</button>
				</>}
			</p>
			<p class="battle-action-grid">
				<button class="button" data-cmd="/switchsides">
					<i class="fa fa-random" aria-hidden></i> {TL`[Switch viewpoint]`}
				</button> {}
				{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">
					<i class="fa fa-random" aria-hidden></i> {TL`[Go to turn]`}
				</button>}
			</p>
		</div>;
	}
	renderMoveButton(props: {
		name: string, cmd: string, type: Dex.TypeName, tags: string, tooltip: string,
		matchups: { name: string, value: number | null, modifiers: string[] }[],
		moveData: { pp?: number, maxpp?: number, disabled?: boolean },
	} | null) {
		if (!props) {
			return <button class="movebutton" disabled>&nbsp;</button>;
		}
		const pp = props.moveData.maxpp ? `${props.moveData.pp!}/${props.moveData.maxpp}` : '\u2014';
		const effectiveness = (value: number | null) => value === null ? 'unknown' : value === 0 ? 'immune' :
			value >= 4 ? 'double-super' : value > 1 ? 'super' : value <= 0.25 ? 'double-resisted' :
			value < 1 ? 'resisted' : 'neutral';
		const kinds = props.matchups.map(matchup => effectiveness(matchup.value));
		const color = kinds.length ? kinds.every(kind => kind === kinds[0]) ? kinds[0] : 'mixed' : undefined;
		return <button
			data-cmd={this.isMobileBattle() && this.mobileInspect ? undefined : props.cmd}
			onClick={this.inspectMobileChoice} data-tooltip={props.tooltip}
			class={`movebutton has-tooltip ${props.moveData.disabled ? 'disabled' : `type-${props.type}`}`}
			aria-disabled={props.moveData.disabled}
			data-effectiveness={color}
		>
			{props.name}<br />
			<small class="type">{props.type} <span class="effectiveness-icon">{props.tags}</span></small> {}
			<small class="pp">{pp}<span class="mobile-pp-label"> PP</span></small><span class="move-spacer">&nbsp;</span>
			{!!props.matchups.length && <span class="move-matchups">
				{props.matchups.map(matchup => <span class={`move-matchup matchup-${effectiveness(matchup.value)}`}>
					{props.matchups.length > 1 && <>{matchup.name}: </>}
					{matchup.value === null ? TL`Check target` : matchup.value === 0 ? TL`No effect` :
					matchup.value > 1 ? TL`Super effective` : matchup.value < 1 ? TL`Not very effective` : TL`Effective`}
					{matchup.value !== null && <> · {matchup.value}×</>}
					{!!matchup.modifiers.length && <span class="move-ability-modifiers">{matchup.modifiers.join(" + ")}</span>}
				</span>)}
			</span>}
		</button>;
	}
	renderPokemonButton(props: {
		pokemon: Pokemon | ServerPokemon | null, cmd: string, noHPBar?: boolean, disabled?: boolean | 'fade', tooltip: string,
	}) {
		const pokemon = props.pokemon;
		if (!pokemon) {
			return <button
				data-cmd={this.isMobileBattle() && this.mobileInspect ? undefined : props.cmd}
				onClick={this.inspectMobileChoice} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
				aria-disabled={props.disabled}
				style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
			>
				{TL`(empty slot)`}
			</button>;
		}

		const teamPokemon = this.isMobileBattle() && props.tooltip.startsWith('switchpokemon|') ?
			pokemon as ServerPokemon : null;
		const dex = this.props.room.battle.dex;
		const battle = this.props.room.battle;
		const clientPokemon = teamPokemon?.active ?
			battle.mySide.active.find(active => active?.ident === teamPokemon.ident) || null : null;
		const teamStats = teamPokemon?.stats ?
			(battle.scene as BattleScene).tooltips.calculateModifiedStats(clientPokemon, teamPokemon) : null;
		const teraUsed = !!teamPokemon && (
			(this.props.room.request?.side?.pokemon || this.team || []).some(member => !!member.terastallized) ||
			battle.mySide.pokemon.some(member => !!member.terastallized)
		);
		const statusLabels: { [status: string]: string } = {
			brn: TL`Burned`, psn: TL`Poisoned`, tox: TL`Badly poisoned`,
			par: TL`Paralyzed`, slp: TL`Asleep`, frz: TL`Frozen`,
		};
		let hpColorClass;
		switch (BattleScene.getHPColor(pokemon)) {
		case 'y': hpColorClass = 'hpbar hpbar-yellow'; break;
		case 'r': hpColorClass = 'hpbar hpbar-red'; break;
		default: hpColorClass = 'hpbar'; break;
		}

		return <button
			data-cmd={this.isMobileBattle() && this.mobileInspect ? undefined : props.cmd}
			onClick={this.inspectMobileChoice} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
			aria-disabled={props.disabled}
			style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
		>
			{PSIcon({ pokemon })}
			<span class="mobile-team-name">{pokemon.name}</span>
			{teamPokemon?.teraType && <span
				class={`mobile-team-tera${teraUsed ? ' used' : ''}`}
				title={teraUsed ? TL`Tera already used` : TL`Tera type`}
			>
				<span>{TL`Tera`}</span> <PSIcon type={teamPokemon.teraType} new tera />
				{teraUsed && <span class="sr-only">{TL`Tera already used`}</span>}
			</span>}
			{!props.noHPBar && <small class="mobile-pokemon-hp">
				{pokemon.fainted ? TL`Fainted` : `${Math.ceil(pokemon.hp / pokemon.maxhp * 100)}% HP`}
				{teamPokemon?.active && !pokemon.fainted && <> · {TL`Active`}</>}
				{pokemon.status && !pokemon.fainted && <> · {statusLabels[pokemon.status] || pokemon.status.toUpperCase()}</>}
			</small>}
			{
				!props.noHPBar && !pokemon.fainted &&
				<span class={hpColorClass}>
					<span style={{ width: this.isMobileBattle() ? `${Math.max(1, pokemon.hp / pokemon.maxhp * 100)}%` :
					Math.round(pokemon.hp * 92 / pokemon.maxhp) || 1 }}
					></span>
				</span>
			}
			{!props.noHPBar && pokemon.status && <span class={`status ${pokemon.status}`}></span>}
			{teamPokemon && <span class="mobile-team-summary">
				<span class="mobile-team-item">{TL`Item`}: <strong>{teamPokemon.item ?
					dex.items.get(teamPokemon.item).name : TL`None`}</strong></span>
				<span class="mobile-team-ability">{TL`Ability`}: {dex.abilities.get(
					teamPokemon.ability || teamPokemon.baseAbility).name}</span>
				{teamStats && <span class="mobile-team-stats" aria-label={TL`Current stats`}>
					<span><small>HP</small><strong>{teamPokemon.hp}/{teamPokemon.maxhp}</strong></span>
					{Dex.statNamesExceptHP.filter(stat => battle.gen !== 1 || stat !== 'spd').map(stat =>
						<span><small>{TL.statShort[battle.gen === 1 && stat === 'spa' ? 'spc' : stat] || stat}</small>
							<strong>{teamStats[stat]}</strong></span>
					)}
				</span>}
				<span class="mobile-team-moves">{teamPokemon.moves.map(move =>
					<span>{dex.moves.get(move).name}</span>
				)}</span>
			</span>}
		</button>;
	}
	renderMoveMenu(choices: BattleChoiceBuilder, overlayVersion?: boolean) {
		const moveRequest = choices.currentMoveRequest()!;

		const canDynamax = moveRequest.canDynamax && !choices.alreadyMax;
		const canMegaEvo = moveRequest.canMegaEvo && !choices.alreadyMega;
		const canMegaEvoX = moveRequest.canMegaEvoX && !choices.alreadyMega;
		const canMegaEvoY = moveRequest.canMegaEvoY && !choices.alreadyMega;
		const canZMove = moveRequest.zMoves && !choices.alreadyZ;
		const canUltraBurst = moveRequest.canUltraBurst;
		const canTerastallize = moveRequest.canTerastallize;

		const maybeDisabled = moveRequest.maybeDisabled;
		const maybeLocked = moveRequest.maybeLocked;

		return <div class="movemenu">
			{maybeDisabled && <p><em class="movewarning">
				{this.renderUIText('mightBeDisabled')}
			</em></p>}
			{maybeLocked && <p><em class="movewarning">
				{this.renderUIText('mightBeLocked')} {}
				<button class="button" data-cmd="/choose testfight">{TL`[Try Fight button]`}</button> {}
				{this.renderUIText('lockedExplanation')}
			</em></p>}
			{!overlayVersion && this.renderMoveControls(moveRequest, choices)}
			<div class="megaevo-box">
				{canDynamax && <label class={`megaevo${choices.current.max ? ' cur' : ''}`}>
					<input type="checkbox" name="max" checked={choices.current.max} onChange={this.toggleBoostedMove} /> {}
					{moveRequest.gigantamax ? TL.tag.gigantamax : TL`Dynamax`}
				</label>}
				{canMegaEvo && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="mega" checked={choices.current.mega} onChange={this.toggleBoostedMove} /> {}
					{TL`Mega Evolution`}
				</label>}
				{canMegaEvoX && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="megax" checked={choices.current.megax} onChange={this.toggleBoostedMove} /> {}
					{TL`Mega Evolution`} X
				</label>}
				{canMegaEvoY && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="megay" checked={choices.current.megay} onChange={this.toggleBoostedMove} /> {}
					{TL`Mega Evolution`} Y
				</label>}
				{canUltraBurst && <label class={`megaevo${choices.current.ultra ? ' cur' : ''}`}>
					<input type="checkbox" name="ultra" checked={choices.current.ultra} onChange={this.toggleBoostedMove} /> {}
					{TL`Ultra Burst`}
				</label>}
				{canZMove && <label class={`megaevo${choices.current.z ? ' cur' : ''}`}>
					<input type="checkbox" name="z" checked={choices.current.z} onChange={this.toggleBoostedMove} /> {}
					{TL`Z-Power`}
				</label>}
				{canTerastallize && <label class={`megaevo${choices.current.tera ? ' cur' : ''}`}>
					<input type="checkbox" name="tera" checked={choices.current.tera} onChange={this.toggleBoostedMove} /> {}
					{TL`Tera ${'{TYPE}'}`.split(/(\{TYPE\})/).map(part => (
						part === '{TYPE}' ? PSIcon({ type: canTerastallize, new: true, tera: true }) : part
					))}
				</label>}
			</div>
			{overlayVersion && this.renderMoveControls(moveRequest, choices)}
		</div>;
	}
	renderMoveControls(active: BattleRequestActivePokemon, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		const dex = battle.dex;
		const pokemonIndex = choices.index();
		const activeIndex = battle.mySide.n > 1 ? pokemonIndex + battle.pokemonControlled : pokemonIndex;
		const serverPokemon = choices.request.side!.pokemon[pokemonIndex];
		const valueTracker = new ModifiableValue(battle, battle.nearSide.active[activeIndex]!, serverPokemon);
		const tooltips = (battle.scene as BattleScene).tooltips;

		if (choices.current.max || (active.maxMoves && !active.canDynamax)) {
			if (!active.maxMoves) {
				return <div class="message-error">{TL`Maxed with no max moves`}</div>;
			}
			const gmax = active.gigantamax && dex.moves.get(active.gigantamax);
			return active.moves.map((moveData, i) => {
				const move = dex.moves.get(moveData.name);
				const [moveType, tags, matchups] = tooltips.getMoveTypeText(move, valueTracker, gmax || true);
				let maxMoveData: { name: string, id: ID } = active.maxMoves![i];
				if (maxMoveData.name !== 'Max Guard') {
					maxMoveData = tooltips.getMaxMoveFromType(moveType, gmax);
				}
				const gmaxTooltip = maxMoveData.id.startsWith('gmax') ? `|${maxMoveData.id}` : ``;
				const tooltip = `maxmove|${moveData.name}|${pokemonIndex}${gmaxTooltip}`;
				return this.renderMoveButton({
					name: TL(dex.moves.get(maxMoveData.name)),
					cmd: `/move ${i + 1} max`,
					type: moveType,
					tags,
					matchups,
					tooltip,
					moveData,
				});
			});
		}

		if (choices.current.z) {
			if (!active.zMoves) {
				return <div class="message-error">{TL`No Z moves`}</div>;
			}
			return active.moves.map((moveData, i) => {
				const zMoveData = active.zMoves![i];
				if (!zMoveData) {
					return this.renderMoveButton(null);
				}
				const specialMove = dex.moves.get(zMoveData.name);
				const move = specialMove.exists ? specialMove : dex.moves.get(moveData.name);
				let moveName = TL(move);
				if (zMoveData.name.startsWith('Z-') && !moveName.startsWith('Z-')) moveName = `Z-${moveName}`;
				const [moveType, tags, matchups] = tooltips.getMoveTypeText(move, valueTracker);
				const tooltip = `zmove|${moveData.name}|${pokemonIndex}`;
				return this.renderMoveButton({
					name: moveName,
					cmd: `/move ${i + 1} zmove`,
					type: moveType,
					tags,
					matchups,
					tooltip,
					moveData: { pp: 1, maxpp: 1 },
				});
			});
		}

		const special = choices.moveSpecial(choices.current);
		return active.moves.map((moveData, i) => {
			const move = dex.moves.get(moveData.name);
			const [moveType, tags, matchups] = tooltips.getMoveTypeText(move, valueTracker);
			const tooltip = `move|${moveData.name}|${pokemonIndex}`;
			return this.renderMoveButton({
				name: TL(move),
				cmd: `/move ${i + 1}${special}`,
				type: moveType,
				tags,
				matchups,
				tooltip,
				moveData,
			});
		});
	}
	renderMoveTargetControls(request: BattleMoveRequest, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		let moveTarget = choices.currentMove()?.target;
		if ((moveTarget === 'adjacentAlly' || moveTarget === 'adjacentFoe') && battle.gameType === 'freeforall') {
			moveTarget = 'normal';
		}
		const moveChoice = choices.stringChoice(choices.current);

		const userSlot = choices.index() + Math.floor(battle.mySide.n / 2) * battle.pokemonControlled;
		const userSlotCross = battle.farSide.active.length - 1 - userSlot;

		return <>
			{battle.farSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentFoe') {
					if (Math.abs(userSlotCross - i) > 1) disabled = true;
				}

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} +${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|1|${i}`,
				});
			}).reverse()}
			<div style={{ clear: 'left' }}></div>
			{battle.nearSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentFoe') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					if (Math.abs(userSlot - i) > 1) disabled = true;
				}
				if (moveTarget !== 'adjacentAllyOrSelf' && userSlot === i) disabled = true;

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} -${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|0|${i}`,
				});
			})}
		</>;
	}
	renderSwitchMenu(
		request: BattleMoveRequest | BattleSwitchRequest, choices: BattleChoiceBuilder, ignoreTrapping?: boolean
	) {
		const numActive = choices.requestLength();
		const maybeTrapped = !ignoreTrapping && choices.currentMoveRequest()?.maybeTrapped;
		const trapped = !ignoreTrapping && !maybeTrapped && choices.currentMoveRequest()?.trapped;
		const isReviving = choices.isReviving();

		return <div class="switchmenu">
			{maybeTrapped && <em class="movewarning">
				{this.renderUIText('mightBeTrapped')}<br />
			</em>}
			{trapped && <em class="movewarning">
				{this.renderUIText('cantSwitchTrapped')}<br />
			</em>}
			{request.side.pokemon.map((serverPokemon, i) => {
				if (this.isMobileBattle() && serverPokemon.fainted && !isReviving) return null;
				let cantSwitch = trapped || i < numActive || choices.alreadySwitchingIn.includes(i + 1) || serverPokemon.fainted;
				if (isReviving) cantSwitch = !serverPokemon.fainted || choices.alreadySwitchingIn.includes(i + 1);
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch ${i + 1}`,
					disabled: cantSwitch,
					tooltip: `switchpokemon|${i}`,
				});
			})}
			{request.ally?.pokemon?.map((serverPokemon, i) => {
				if (this.isMobileBattle() && serverPokemon.fainted) return null;
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch notMine`,
					disabled: true,
					tooltip: `allypokemon|${i}`,
				});
			})}
		</div>;
	}
	renderTeamPreviewChooser(request: | BattleTeamRequest, choices: BattleChoiceBuilder) {
		return request.side.pokemon.map((serverPokemon, i) => {
			const cantSwitch = choices.alreadySwitchingIn.includes(i + 1);
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${i + 1}`,
				disabled: cantSwitch && 'fade',
				tooltip: `switchpokemon|${i}`,
			});
		});
	}
	renderTeamList(overlayVersion = false) {
		const team = this.team;
		if (!team) return;
		return <div class="switchcontrols">
			{!overlayVersion && <h3 class="switchselect">{TL`[Team]`}</h3>}
			<div class="switchmenu">
				{team.map((serverPokemon, i) => {
					if (this.isMobileBattle() && serverPokemon.fainted) return null;
					return this.renderPokemonButton({
						pokemon: serverPokemon,
						cmd: "",
						disabled: true,
						tooltip: `switchpokemon|${i}`,
					});
				})}
			</div>
		</div>;
	}
	renderChosenTeam(request: BattleTeamRequest, choices: BattleChoiceBuilder) {
		return choices.alreadySwitchingIn.map(slot => {
			const serverPokemon = request.side.pokemon[slot - 1];
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${slot}`,
				tooltip: `switchpokemon|${slot - 1}`,
			});
		});
	}
	renderBackButton() {
		return <span class={this.isMobileBattle() ? 'battle-action-grid' : undefined}>
			<button data-cmd="/cancelone" class="button">
				<i class="fa fa-chevron-left" aria-hidden></i> {TL`[Back]`}
			</button>
		</span>;
	}
	renderOldChoices(request: BattleRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		if (!choices) return null; // should not happen
		if (
			(request.requestType !== 'move' && request.requestType !== 'switch' && request.requestType !== 'team') ||
			choices.isEmpty()
		) {
			return null;
		}

		let buf: preact.ComponentChild[] = [
			this.renderBackButton(), ' ',
		];
		if (choices.isDone() && (
			choices.noCancel || this.props.room.battle.hardcoreMode ||
			(choices.choices.length <= 1 && !overlayVersion)
		)) {
			buf = [];
		}

		if (choices.serializedChoice) {
			if (choices.serializedChoice === 'default') {
				return [BattleTextParser.ui('autoChoice'), <br />];
			}
			return [BattleTextParser.ui('unrecognizedChoice') + ' ', <code>{choices.serializedChoice}</code>, <br />];
		}

		const battle = this.props.room.battle;
		const pickedNames: string[] = [];
		for (let i = 0; i < choices.choices.length; i++) {
			const choiceString = choices.choices[i];
			if (choiceString === "testfight") {
				buf.push(this.renderUIText('lockedIntoMove', { POKEMON: request.side.pokemon[i].name }));
				return buf;
			}
			let choice;
			try {
				choice = choices.parseChoice(choiceString, i);
			} catch (err: any) {
				buf.push(<span class="message-error">{err.message}</span>);
			}
			if (!choice) continue;
			const pokemon = request.side.pokemon[i];
			const active = request.requestType === 'move' ? request.active[i] : null;
			if (choice.choiceType === 'move') {
				let actions = '';
				if (choice.mega) actions += BattleTextParser.ui('actionMegaEvolve');
				if (choice.megax) actions += BattleTextParser.ui('actionMegaEvolveX');
				if (choice.megay) actions += BattleTextParser.ui('actionMegaEvolveY');
				if (choice.ultra) actions += BattleTextParser.ui('actionUltraBurst');
				if (choice.tera) actions += BattleTextParser.ui('actionTerastallize', { TYPE: active?.canTerastallize || '???' });
				if (choice.max && active?.canDynamax) {
					actions += BattleTextParser.ui(active?.gigantamax ? 'actionGigantamax' : 'actionDynamax');
				}
				let target = '';
				if (choice.targetLoc > 0) {
					const targetPokemon = battle.farSide.active[choice.targetLoc - 1];
					target = targetPokemon ?
						BattleTextParser.ui('atTarget', { TARGET: targetPokemon.name }) :
						BattleTextParser.ui('atSlot', { NUMBER: `${choice.targetLoc}` });
				} else if (choice.targetLoc < 0) {
					const targetPokemon = battle.nearSide.active[-choice.targetLoc - 1];
					const isAlly = battle.gameType !== 'freeforall';
					if (targetPokemon) {
						target = BattleTextParser.ui(isAlly ? 'atAllyTarget' : 'atTarget', { TARGET: targetPokemon.name });
					} else {
						target = BattleTextParser.ui(isAlly ? 'atAllySlot' : 'atSlot', { NUMBER: `${choice.targetLoc}` });
					}
				}
				buf.push(this.renderUIText('willUseMove', {
					POKEMON: pokemon.name,
					ACTIONS: actions,
					MOVE: choices.currentMove(choice, i)?.name || '???',
					AT: target,
				}));
			} else if (choice.choiceType === 'switch') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				buf.push(this.renderUIText(choices.isReviving(i) ? 'willRevive' : 'willSwitch', {
					POKEMON: pokemon.name, TARGET: target.name,
				}));
			} else if (choice.choiceType === 'shift') {
				buf.push(this.renderUIText('willShift', { POKEMON: pokemon.name }));
			} else if (choice.choiceType === 'team') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				pickedNames.push(target.name);
			}
			if (!pickedNames.length) buf.push(<br />);
		}
		if (pickedNames.length) {
			const pickedList = pickedNames.map(name => `**${name}**`).join(BattleTextParser.ui('listComma'));
			buf.push(this.renderUIText('youPicked', { POKEMON: pickedList }), <br />);
		}
		return buf;
	}
	overlayControlClass(overlay: 'move' | 'switch') {
		return `button ${overlay}-button${this.props.room.overlayActive === overlay ? ' cur' : ''}`;
	}
	renderPlayerAnimationControls(overlayVersion = false) {
		const room = this.props.room;
		if (overlayVersion) {
			const canSkip = !room.battle.hardcoreMode;
			return <>
				{canSkip && <div class="overlay-controls-skip">
					<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />{TL`[Skip]`}</button>
				</div>}
			</>;
		}
		return <div class="inline-controls">
			{!room.battle.hardcoreMode && <div class="battle-action-grid battle-animation-actions">
				<button class="button" data-cmd="/ffto end">
					<i class="fa fa-fast-forward" aria-hidden></i><br />{TL`[Skip animation]`}
				</button>
			</div>}
			{this.renderTeamList()}
		</div>;
	}
	renderPlayerMoveControls(request: BattleMoveRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const room = this.props.room;
		const index = choices.index();
		const pokemon = request.side.pokemon[index];

		if (choices.current.move) {
			const moveName = choices.currentMove()?.name;
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class="button move-button cur"><strong>{TL`[Battle]`}</strong></button> {}
						<button class="button switch-button disabled"><strong>{TL`[Switch]`}</strong></button>
					</div>
					<div class="targetcontrols">
						<p class="overlay-message">
							{this.renderOldChoices(request, choices, true)}
							{this.renderUIText('moveTarget', { POKEMON: pokemon.name, MOVE: moveName })}
						</p>
						<div class="switchmenu">
							{this.renderMoveTargetControls(request, choices)}
						</div>
					</div>
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					{this.renderUIText('moveTarget', { POKEMON: pokemon.name, MOVE: moveName })} {}
				</div>
				<div class="switchcontrols">
					<div class="switchmenu">
						{this.renderMoveTargetControls(request, choices)}
					</div>
				</div>
			</div>;
		}

		const canShift = room.battle.gameType === 'triples' && index !== 1;

		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class={this.overlayControlClass('move')} data-cmd="/movemenu"><strong>{TL`[Battle]`}</strong></button> {}
					<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>{TL`[Switch]`}</strong></button>
				</div>
				{!room.overlayActive && <div class="whatdo">
					{this.renderOldChoices(request, choices, true)}
					{this.renderUIText('whatDo', { POKEMON: pokemon.name })}
				</div>}
				{room.overlayActive === 'move' && <div class="movecontrols">
					{this.renderMoveMenu(choices, true)}
				</div>}
				{room.overlayActive === 'switch' && <div class="switchcontrols">
					{canShift && (
						<button data-cmd="/shift">{TL`[Move to center]`}</button>
					)}
					{this.renderSwitchMenu(request, choices)}
				</div>}
			</>;
		}
		return <div class={`inline-controls mobile-choice-${this.mobilePane}`}>
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				{this.renderUIText('whatDo', { POKEMON: pokemon.name })}
			</div>
			<div class="movecontrols">
				<h3 class="moveselect">{TL`[Battle]`}</h3>
				{this.renderMoveMenu(choices)}
			</div>
			<div class="switchcontrols">
				{canShift && [
					<h3 class="shiftselect">{TL`[Shift]`}</h3>,
					<button data-cmd="/shift">{TL`[Move to center]`}</button>,
				]}
				<h3 class="switchselect">{TL`[Switch]`}</h3>
				{this.renderSwitchMenu(request, choices)}
			</div>
		</div>;
	}
	renderPlayerSwitchControls(request: BattleSwitchRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const pokemon = request.side.pokemon[choices.index()];
		const prompt = choices.isReviving() ?
			this.renderUIText('reviveWho', { POKEMON: pokemon.name }) :
			this.renderUIText('replaceWho', { POKEMON: pokemon.name });
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>{TL`[Switch]`}</strong></button>
				</div>
				<div class="switchcontrols">
					<p class="overlay-message">
						{this.renderOldChoices(request, choices, true)}
						{prompt}
					</p>
					{this.renderSwitchMenu(request, choices, true)}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">{TL`[Switch]`}</h3>
				{this.renderSwitchMenu(request, choices, true)}
			</div>
		</div>;
	}
	renderPlayerTeamPreviewControls(request: BattleTeamRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const prompt = choices.alreadySwitchingIn.length > 0 ? (
			[this.renderBackButton(),
				" ", this.renderUIText('teamRest'), " "]
		) : (
			[this.renderUIText('teamStart'), " "]
		);
		const chosenTeamSizeLabel = (request.chosenTeamSize || 0) > 1 ? ` / ${request.chosenTeamSize!}` : '';
		const chooseLabel = (choices.alreadySwitchingIn.length <= 0 ?
			BattleTextParser.ui('chooseLead') : BattleTextParser.ui('chooseSlot', { NUMBER: `${choices.alreadySwitchingIn.length + 1}` })) + chosenTeamSizeLabel;
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>{TL`[Team]`}</strong></button>
				</div>
				<div class="teamcontrols">
					<p class="overlay-message">{prompt}</p>
					<h3 class="switchselect">{chooseLabel}</h3>
					<div class="switchmenu">
						{this.renderTeamPreviewChooser(request, choices)}
						<div style="clear:left"></div>
					</div>
					{choices.alreadySwitchingIn.length > 0 && <>
						<h3 class="switchselect">{this.renderUIText('teamSoFar')}</h3>
						<div class="switchmenu">
							{this.renderChosenTeam(request, choices)}
						</div>
					</>}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">
					{chooseLabel}
				</h3>
				<div class="switchmenu">
					{this.renderTeamPreviewChooser(request, choices)}
					<div style="clear:left"></div>
				</div>
			</div>
			<div class="switchcontrols">
				{choices.alreadySwitchingIn.length > 0 && <h3 class="switchselect">
					{this.renderUIText('teamSoFar')}
				</h3>}
				<div class="switchmenu">
					{this.renderChosenTeam(request, choices)}
				</div>
			</div>
		</div>;
	}
	renderPlayerControls(request: BattleRequest, overlayVersion = false) {
		const room = this.props.room;
		const atEnd = room.battle.atQueueEnd;
		if (!atEnd) return this.renderPlayerAnimationControls(overlayVersion);

		let choices = room.choices;
		if (!choices) return 'Error: Missing BattleChoiceBuilder';
		if (choices.request !== request) {
			choices = new BattleChoiceBuilder(request);
			room.choices = choices;
			room.overlayActive = null;
		}

		if (choices.isDone()) {
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>{TL`[Team]`}</strong></button>
					</div>
					{!room.overlayActive && <div class="whatdo">
						{this.renderOldChoices(request, choices, true)}
					</div>}
					{room.overlayActive === 'switch' && this.renderTeamList(true)}
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					<em>{this.renderUIText('waitingOpponent')}</em>
				</div>
				{!choices.noCancel && !room.battle.hardcoreMode && <div class="battle-action-grid">
					<button data-cmd="/cancel" class="button">{TL`[Cancel]`}</button>
				</div>}
				{this.renderTeamList()}
			</div>;
		}
		if (request.side) {
			room.battle.myPokemon = request.side.pokemon;
			this.team = request.side.pokemon;
		}
		switch (request.requestType) {
		case 'move':
			return this.renderPlayerMoveControls(request, choices, overlayVersion);
		case 'switch':
			return this.renderPlayerSwitchControls(request, choices, overlayVersion);
		case 'team':
			return this.renderPlayerTeamPreviewControls(request, choices, overlayVersion);
		}
		return null;
	}

	renderAfterBattleControls() {
		const room = this.props.room;
		const isNotTiny = room.width > 700;
		return <div class="inline-controls battle-result-controls">
			<div class="battle-result-primary">
				<button class="button" data-cmd="/play">
					<i class="fa fa-undo" aria-hidden></i> {TL`[Replay]`}
				</button>
				{room.side && <button class="button" data-cmd={`/closeand /challenge ${room.battle.farSide.id},${room.battle.tier}`}>
					{TL`[Rematch]`}
				</button>}
				<button class="button" data-cmd="/close">{TL`[Main menu]`}</button>
			</div>
			<div class="battle-result-secondary">
				<a onClick={this.handleDownloadReplay} href={`//${Config.routes.replays}/download`} class="button replayDownloadButton">
					<i class="fa fa-download" aria-hidden></i> Download replay
				</a>
				<button class="button" data-cmd="/savereplay">
					<i class="fa fa-upload" aria-hidden></i> {TL`[Upload and share replay]`}
				</button>
				{isNotTiny && !room.battle.hardcoreMode && <>
					<button class="button" data-cmd="/ffto 0">{TL`[First turn]`}</button>
					<button class="button" data-cmd="/ffto -1">{TL`[Prev turn]`}</button>
				</>}
				{!room.side && <>
					<button class="button" data-cmd="/switchsides">{TL`[Switch viewpoint]`}</button>
					{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">{TL`[Go to turn]`}</button>}
				</>}
			</div>
		</div>;
	}

	handleDownloadReplay = (e: MouseEvent) => {
		let room = this.props.room;
		const target = e.currentTarget as HTMLAnchorElement;
		// download replay
		let filename = (room.battle.tier || 'Battle').replace(/[^A-Za-z0-9]/g, '');
		let date = new Date();
		filename += `-${date.getFullYear()}`;
		filename += `-${date.getMonth() >= 9 ? '' : '0'}${date.getMonth() + 1}`;
		filename += `-${date.getDate() >= 10 ? '' : '0'}${date.getDate()}`;
		filename += '-' + toID(room.battle.p1.name);
		filename += '-' + toID(room.battle.p2.name);
		target.href = window.BattleLog.createReplayFileHref(room);
		target.download = filename + '.html';
		e.stopPropagation();
	};

	override render() {
		this.updateLayout();
		const room = this.props.room;
		const id = `room-${room.id}`;
		const hardcoreStyle = room.battle?.hardcoreMode ? <style
			dangerouslySetInnerHTML={{ __html: `#${id} .battle .turn, #${id} .battle-history { display: none !important; }` }}
		></style> : null;
		const { layout, battleHeight, battleWidth, overlayControls } = this.chooseLayout();
		const overlayVersion = overlayControls && !!room.battle && !!room.side && !!room.request && !room.battle.ended;

		if (this.isMobileBattle()) {
			const showingLog = this.mobilePane === 'log';
			const showingTools = this.mobilePane === 'tools';
			const canInspect = room.side && room.battle?.atQueueEnd && !room.battle.ended &&
				room.choices && !room.choices.isDone();
			const mustSwitch = room.request?.requestType === 'switch';
			const pane = mustSwitch && this.mobilePane === 'moves' ? 'switch' : this.mobilePane;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div class={`mobile-battle${room.width > room.height ? ' mobile-battle-landscape' : ''}`}>
					{this.mobileDetailHTML !== null && <dialog
						class="mobile-battle-details" aria-label={TL`Battle details`}
						ref={(dialog: HTMLDialogElement | null) => {
							if (!dialog) return;
							dialog.oncancel = this.closeMobileDetails;
							dialog.onclose = this.closeMobileDetails;
							if (!dialog.open) dialog.showModal();
						}}
					>
						<div
							class="mobile-details-heading" onTouchStart={this.startMobileSwipe}
							onTouchEnd={this.endMobileSwipe} onTouchCancel={this.cancelMobileSwipe}
						>
							<strong>{TL`Battle details`}</strong>
							<button class="button" onClick={this.closeMobileDetails} autofocus>{TL`Close`}</button>
						</div>
						<div class="mobile-details-content" dangerouslySetInnerHTML={{ __html: this.mobileDetailHTML }} />
					</dialog>}
					<div class="mobile-battle-field" style={{ height: battleHeight, width: battleWidth }}>
						<BattleDiv room={room} />
					</div>
					{room.battle?.started && <div class="mobile-matchup">
						{[room.battle.nearSide, room.battle.farSide].map((side, sideIndex) => <div>
							{side.active.map((pokemon, index) => pokemon && <button
								class="has-tooltip" data-tooltip={`activepokemon|${sideIndex}|${index}`}
								onClick={this.showMobileDetails} aria-label={TL`View stats for ${pokemon.name}`}
							>
								<strong>{pokemon.name}</strong>
								<span>{pokemon.fainted ? TL`Fainted` : pokemon.getHPText()} {pokemon.status.toUpperCase()}</span>
								<span class="mobile-pokemon-stats"><i class="fa fa-info-circle" aria-hidden></i> {TL`Stats`}</span>
							</button>)}
						</div>)}
						<BattleTeraTracker battle={room.battle} />
					</div>}
					<div class="mobile-battle-status">
						<strong>{room.battle?.ended ? TL`Battle ended` : !room.side ? TL`Watching` :
						room.battle && !room.battle.atQueueEnd ? TL`Turn playing` :
						room.choices && !room.choices.isDone() && room.request?.requestType !== 'wait' ?
							TL`Your turn` : TL`Waiting`}</strong>
						{room.battle && !room.battle.ended && <TimerButton room={room} top={0} />}
						{room.isPlaying() && <button class="button mobile-forfeit" data-cmd="/close">
							<i class="fa fa-flag" aria-hidden></i> {TL`Forfeit & close`}
						</button>}
					</div>
					<div
						class="mobile-battle-tabs" onTouchStart={this.startMobileSwipe}
						onTouchEnd={this.endMobileSwipe} onTouchCancel={this.cancelMobileSwipe} role="group" aria-label={TL`Battle view`}
					>
						<button value="moves" aria-pressed={pane === 'moves'} onClick={this.setMobilePane}>
							<i class="fa fa-bolt" aria-hidden></i> {room.battle?.ended ? TL`Results` : room.side ? TL`Moves` : TL`Battle`}
						</button>
						<button value="switch" aria-pressed={pane === 'switch'} onClick={this.setMobilePane}>
							<i class="fa fa-exchange" aria-hidden></i> {TL`Team`}
						</button>
						<button value="tools" aria-pressed={showingTools} onClick={this.setMobilePane}>
							<i class="fa fa-calculator" aria-hidden></i> Tools
						</button>
						<button value="log" aria-pressed={showingLog} onClick={this.setMobilePane}>
							<i class="fa fa-list" aria-hidden></i> {TL`Log`}
						</button>
						{canInspect && <button
							class="mobile-inspect-toggle" aria-label={this.mobileInspect ? TL`Done inspecting` : TL`Inspect choices`}
							title={this.mobileInspect ? TL`Done inspecting` : TL`Inspect choices`}
							aria-pressed={this.mobileInspect} onClick={this.toggleMobileInspect}
						>
							<i class={`fa fa-${this.mobileInspect ? 'check' : 'search'}`} aria-hidden></i>
						</button>}
					</div>
					<div
						class="mobile-battle-body" onTouchStart={this.startMobileSwipe}
						onTouchEnd={this.endMobileSwipe} onTouchCancel={this.cancelMobileSwipe}
					>
						<div class="mobile-battle-actions" hidden={showingLog || showingTools}>
							{this.mobileInspect && <p class="mobile-inspect-hint" role="status">
								{TL`Inspecting — tap a choice for details. Tap ✓ to choose moves again.`}
							</p>}
							<div class="battle-controls" role="complementary" aria-label={TL`Battle controls`}>
								{this.renderControls()}
								{this.renderConnectError()}
							</div>
						</div>
						<div class="mobile-battle-tools" hidden={!showingTools}>
							{mustSwitch && <button class="button battle-tools-switch-reminder" value="switch" onClick={this.setMobilePane}>
								Choose a Pokémon to continue →
							</button>}
							{this.battleToolsOpened && room.battle && <BattleToolsPanel battle={room.battle} />}
						</div>
						<div class="mobile-battle-chat" hidden={!showingLog}>
							<ChatLog class="battle-log" room={room} noSubscription hasPreempt />
							<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout />
						</div>
					</div>
				</div>
			</PSPanelWrapper>;
		}

		if (layout === 'scrolling') {
			// low-width-low-height layout
			// TODO: nicer phone horizontal layout
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<ChatLog
					class="battle-log hasuserlist" room={room} noSubscription hasPreempt bottom={0}
				>
					<div style="height:18px;position:relative">
						<ChatUserList room={room} top={0} minimized />
					</div>
					<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />
					<div style={`height:${battleHeight}px;width:${battleWidth}px;margin: 0 auto;position:relative`}>
						<BattleDiv room={room} />
					</div>
					{overlayVersion && <div class="overlay-controls" style="position:relative;height:0">
						{this.renderControls(true)}
					</div>}
					<div
						class={`battle-controls inline-battle${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label={TL`Battle controls`}
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (layout === 'top-and-bottom') {
			// phone vertical layout
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div style={`position:relative;height:${battleHeight}px;width:${battleWidth}px;margin:0 auto`}>
					<BattleDiv room={room} />
				</div>
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:100%;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<ChatLog
					class="battle-log hasuserlist" room={room} top={battleHeight} noSubscription hasPreempt
				>
					<div
						class={`battle-controls${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label={TL`Battle controls`}
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />
				<ChatUserList room={room} top={battleHeight} minimized />
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={battleHeight + 7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (room.width < 500) {
			// oldclient phone layout
			const showingChat = this.mobileChatShown;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div class="scrollable-battle-container" style={`width:${battleWidth}px;${showingChat ? 'display:none;' : ''}`}>
					<BattleDiv room={room} />
					{overlayVersion && <div
						class="overlay-controls"
						style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
					>
						{this.renderControls(true)}
					</div>}
					<div class="battle-controls-container">
						<div
							class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
							role="complementary" aria-label={TL`Battle controls`}
							style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
						>
							{(room.battle && !room.battle.ended && room.request &&
								room.battle.mySide.id === PS.user.userid) && <TimerButton room={room} top={0} />}
							{this.renderControls(false, overlayVersion)}
							{this.renderConnectError()}
						</div>
					</div>
				</div>
				<div style={!showingChat ? 'display:none;' : ''}>
					<ChatLog class="battle-log hasuserlist" room={room} noSubscription hasPreempt />
					<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} tinyLayout />
					<ChatUserList room={room} minimized />
				</div>
				{showingChat ? (
					<button class="battle-chat-toggle button" name="hideChat" onClick={this.showMobileBattle}>
						{TL`Battle`} <i class="fa fa-caret-right" aria-hidden></i>
					</button>
				) : (
					<button class="battle-chat-toggle button" name="showChat" onClick={this.showMobileChat}>
						<i class="fa fa-caret-left" aria-hidden></i> {TL`Chat`}
					</button>
				)}
			</PSPanelWrapper>;
		}

		// regular layout
		return <PSPanelWrapper room={room} focusClick noScroll="hidden">
			{hardcoreStyle}
			<div class="scrollable-battle-container" style={`width:${battleWidth}px`}>
				<BattleDiv room={room} />
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<div class="battle-controls-container">
					<div
						class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
						role="complementary" aria-label={TL`Battle controls`}
						style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
					>
						{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
							<TimerButton room={room} top={0} />}
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</div>
			</div>
			<ChatLog
				class="battle-log hasuserlist" room={room} left={battleWidth} noSubscription hasPreempt
			>
				{}
			</ChatLog>
			<ChatTextEntry
				room={room} onMessage={this.send} onKey={this.onKey} left={battleWidth} tinyLayout={room.width < battleWidth + 340}
			/>
			<ChatUserList room={room} left={battleWidth} minimized />
		</PSPanelWrapper>;
	}
}

PS.addRoomType(BattlePanel, BattlesPanel);
